import { cn } from "@/lib/cn";
import type { ChainConfig } from "@atelier/config";

type ExplorerLinkProps = {
  chain: ChainConfig | undefined;
  kind: "address" | "tx" | "token";
  value: string;
  /** Visible label. Defaults to a truncated value when omitted. */
  label?: React.ReactNode;
  className?: string;
  truncate?: boolean;
};

function truncateMiddle(s: string, head = 6, tail = 4): string {
  if (s.length <= head + tail + 1) return s;
  return `${s.slice(0, head)}…${s.slice(-tail)}`;
}

/**
 * Renders an external link to the active chain's block explorer for an
 * address, tx hash, or token (token = address + id encoded as `address/id`
 * by the caller). When the chain has no explorerUrl, falls back to plain text.
 */
export function ExplorerLink({
  chain,
  kind,
  value,
  label,
  className,
  truncate = true,
}: ExplorerLinkProps) {
  const display = label ?? (truncate ? truncateMiddle(value) : value);
  if (!chain?.explorerUrl) {
    return <span className={cn("font-mono", className)}>{display}</span>;
  }
  const base = chain.explorerUrl.replace(/\/$/, "");
  const href =
    kind === "address" ? `${base}/address/${value}`
    : kind === "tx" ? `${base}/tx/${value}`
    : `${base}/token/${value}`;
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={cn(
        "font-mono underline-offset-2 hover:underline text-foreground/90 hover:text-foreground inline-flex items-center gap-1",
        className,
      )}
      title={value}
    >
      {display}
      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M7 17 17 7" />
        <path d="M8 7h9v9" />
      </svg>
    </a>
  );
}
